var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// entities/mongoEntity.ts
import "rxjs";
import { Int } from "../baseqlTypes.js";
import { Arg } from "../decorators/arg.js";
import { Entity } from "../decorators/entity.js";
import { Field } from "../decorators/field.js";
import { Mutation, Query, Subscription } from "../decorators/operations.js";
import {
  countAllEntities,
  createEntity,
  getAllEntities,
  getByID,
  removeEntity,
  subscribeAllEntities,
  subscribeByID,
  subscribeEntityCount,
  updateEntity
} from "../operations/mongo.js";
import {
  countAllEntitiesResolver,
  createEntityResolver,
  findEntityByTextResolver,
  getAllEntitiesResolver,
  getEntityByIDResolver,
  removeEntityResolver,
  subscribeAllEntitiesResolver,
  subscribeEntityByIDResolver,
  subscribeEntityCountResolver,
  subscribePaginated,
  subscribePaginatedAggregation,
  updateEntityResolver
} from "../server/resolver/mongo.js";
import { AdornisEntity } from "./adornisEntity.js";
import { logger } from "@adornis/base/logging.js";
import { MongoDataLoader } from "../server/data-loader.js";
var MongoEntity = class extends AdornisEntity {
  static resolveID(entity) {
    return entity._id;
  }
  /**
   * @see MongoDataLoader
   * @deprecated use the `DataLoadedField` decorators for the fields or the global `loadMongoData` function
   */
  static getDataloader() {
    logger.error(
      new Error(
        `${this._class}.getDataloader is deprecated, use the DataLoadedField decorator or the loadMongoData helper function`
      )
    );
    return MongoDataLoader.getCollectionInstance(this);
  }
  static getByID(id) {
    return getByID(this, id);
  }
  static subscribeByID(id) {
    return subscribeByID(this, id);
  }
  static getEntityByID(entityClass, ids) {
    return getEntityByIDResolver(entityClass, ids);
  }
  static getAllEntities(entityClass, skip = 0, limit = 1e3, excludeIDs = []) {
    return getAllEntitiesResolver(entityClass, skip, limit, excludeIDs);
  }
  static countAllEntities(entityClass) {
    return countAllEntitiesResolver(entityClass);
  }
  static findEntityByText(entityClass, searchFields, searchText, regexOptions) {
    return findEntityByTextResolver(entityClass, searchFields, searchText, regexOptions);
  }
  static subscribeEntityByID(entityClass, ids) {
    return subscribeEntityByIDResolver(entityClass, ids);
  }
  static subscribeAllEntities(entityClass, skip = 0, limit = 1e3, excludeIDs = []) {
    return subscribeAllEntitiesResolver(entityClass, skip, limit, excludeIDs);
  }
  static subscribeEntityCount(entityClass, excludeIDs = []) {
    return subscribeEntityCountResolver(entityClass, excludeIDs);
  }
  /**
   * Subscribes a given query with skip and limit options.
   *
   * This function applies the publishFilter of the class it is called on.
   *
   * @deprecated use the functional version of this exported from \@adornis/baseql/server/resolver/mongo
   */
  static subscribePaginated(skip, limit, selector, options = {}) {
    return subscribePaginated(this, skip, limit, selector, options);
  }
  /**
   * Subscribe a given query and execute an aggregation whenever the subscribed query receives an update.
   * Skip and limit are applied after the aggregation steps and **NOT** to the selector as the aggregation could alter the data.
   *
   * This function applies the publishFilter of the class it is called on.
   *
   * @deprecated use the functional version of this exported from \@adornis/baseql/server/resolver/mongo
   */
  static subscribePaginatedAggregation(skip, limit, selector, aggregationStagesAfterSelection) {
    return subscribePaginatedAggregation(this, skip, limit, selector, aggregationStagesAfterSelection);
  }
  static updateEntity(entityClass, entity) {
    return updateEntityResolver(entity);
  }
  static createEntity(entityClass, entity) {
    return createEntityResolver(entity);
  }
  /**
   * Function to transform the entity *on the server* before it is updated by the updateEntity mutation.
   * Will be passed the old entity as retrieved from the database and the update which will be applied.
   *
   * **This will run before any permission checks are made so you should not include any side-effects in this hook!**
   *
   * _Note: the update argument can be a partial entity, as save strips all undefined and resolved fields before sending the object to the server._
   */
  static async transformBeforeUpdate(oldEntity, update) {
  }
  /**
   * Function to execute logic *on the server* before an entity is updated by the updateEntity mutation.
   * Will be passed the old entity as retrieved from the database and the update which will be applied.
   *
   * Permission checks and transforms are applied when this runs, so you can be sure the update will be saved after this.
   *
   * _Note: the update argument can be a partial entity, as save strips all undefined and resolved fields before sending the object to the server._
   */
  static async beforeUpdate(oldEntity, update) {
  }
  /**
   * Function to execute logic *on the server* after an entity is successfully updated by the updateEntity mutation.
   * Will be passed the saved entity.
   */
  static async afterUpdate(oldEntity, newEntity, result) {
  }
  /**
   * Function to execute logic *on the server* before an entity is then saved by the createEntity mutation.
   * Will be passed the entity to be saved.
   */
  static async beforeCreate(entity) {
  }
  /**
   * Function to execute logic *on the server* after an entity is successfully created by the createEntity mutation.
   * Will be passed the saved entity.
   */
  static async afterCreate(entity, result) {
  }
  static removeEntity(entityClass, id) {
    return removeEntityResolver(entityClass, id);
  }
  /**
   * Function to execute logic *on the server* before an entity is removed by the removeEntity mutation.
   * Will be passed the id to be removed.
   *
   * Note: the argument can be an already non existent id.
   *
   * **This will no longer be neccessary after input unions are implemented** as you can then just define your own remove mutation
   */
  static async beforeRemove(id, entityData) {
  }
  /**
   * Function to execute logic *on the server* after an entity is successfully removed by the removeEntity mutation.
   * Will be passed the id to be removed.
   *
   * Note: the argument can be an already non existent id.
   *
   * **This will no longer be neccessary after input unions are implemented** as you can then just define your own remove mutation
   */
  static async afterRemove(id, entityData) {
  }
  /**
   * Find all entities of this class.
   *
   * Shortcut for `findAllEntities` which handles passing the correct entityClass and selectionSet for you.
   *
   * Can be passed a generic to force the return type to be something other than MongoEntity
   *
   * excludeIDs parameter excludes a given set of ids from the result.
   *
   * @deprecated use the functional version `getAllEntities` exported from \@adornis/baseql/operations/mongo
   */
  static getAll(skip = 0, limit = 1e3, excludeIDs) {
    return getAllEntities(this, skip, limit, excludeIDs);
  }
  /**
   * Subscribe all entities of this class.
   *
   * Shortcut for `subscribeAllEntities` which handles passing the correct entityClass and selectionSet for you.
   *
   * Can be passed a generic to force the return type to be something other than MongoEntity
   *
   * excludeIDs parameter excludes a given set of ids from the result.
   *
   * @deprecated use the functional version `subscribeAllEntities` exported from \@adornis/baseql/operations/mongo
   */
  static subscribeAll(skip = 0, limit = 1e3, excludeIDs) {
    return subscribeAllEntities(this, skip, limit, excludeIDs);
  }
  /**
   * Count all entities of this class.
   *
   * Shortcut for `countAllEntities` which handles passing the correct entityClass for you.
   *
   * @deprecated use the functional version `countAllEntities` exported from \@adornis/baseql/operations/mongo
   */
  static count() {
    return countAllEntities(this);
  }
  /**
   * Subscribe the count of all entities of this class.
   *
   * Shortcut for `subscribeEntityCount` which handles passing the correct entityClass for you.
   *
   * @deprecated use the functional version `subscribeEntityCount` exported from \@adornis/baseql/operations/mongo
   */
  static subscribeCount() {
    return subscribeEntityCount(this);
  }
  /**
   * Save this entity.
   *
   * Shortcut for `updateEntity` which handles passing the entity for you.
   *
   * Returns 1 if the entity has been successfully updated.
   *
   * Throws if _id is not set or no document with this _id exists in the db.
   * If no document exists create the entity via the `create` method.
   *
   * If you overwrite this further down the inheritance chain and you do not want to call super.save(),
   * please call this._save() instead.
   * This ensures all operations regarding the EntityStore are properly executed.
   */
  async save() {
    if (!this._id)
      throw new Error("Use this.create() to create new entities, .save() is only for updating data.");
    await this.commit();
    const matchedCount = await updateEntity(this)();
    await super.save();
    return matchedCount;
  }
  /**
   * Create this entity and write it to the database.
   *
   * Shortcut for `createEntity` which handles passing the entity for you.
   *
   * Will return the id of the entity once successfully written to db.
   */
  async create() {
    await super.commit();
    const _id = await createEntity(this)();
    this._id = _id;
    await super.save();
    return _id;
  }
  /**
   * Remove this entity.
   *
   * Shortcut for `removeEntity` which handles passing the correct id for you.
   */
  async remove() {
    super.remove();
    return removeEntity(this.constructor, this._id)();
  }
};
MongoEntity._collectionName = "";
MongoEntity._class = "MongoEntity";
__decorateClass([
  Field((type) => String, { default: (v) => v ?? null })
], MongoEntity.prototype, "_id", 2);
__decorateClass([
  Query((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("ids", (type) => [String]))
], MongoEntity, "getEntityByID", 1);
__decorateClass([
  Query((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("skip", (type) => Int)),
  __decorateParam(2, Arg("limit", (type) => Int)),
  __decorateParam(3, Arg("excludeIDs", (type) => [String]))
], MongoEntity, "getAllEntities", 1);
__decorateClass([
  Query((type) => Int),
  __decorateParam(0, Arg("entityClass", (type) => String))
], MongoEntity, "countAllEntities", 1);
__decorateClass([
  Query((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("searchFields", (type) => [String])),
  __decorateParam(2, Arg("searchText", (type) => String)),
  __decorateParam(3, Arg("regexOptions", (type) => String))
], MongoEntity, "findEntityByText", 1);
__decorateClass([
  Subscription((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("ids", (type) => [String]))
], MongoEntity, "subscribeEntityByID", 1);
__decorateClass([
  Subscription((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("skip", (type) => Int)),
  __decorateParam(2, Arg("limit", (type) => Int)),
  __decorateParam(3, Arg("excludeIDs", (type) => [String]))
], MongoEntity, "subscribeAllEntities", 1);
__decorateClass([
  Subscription((type) => Int),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("excludeIDs", (type) => [String]))
], MongoEntity, "subscribeEntityCount", 1);
__decorateClass([
  Mutation((type) => Int),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("entity", (type) => MongoEntity))
], MongoEntity, "updateEntity", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("entity", (type) => MongoEntity))
], MongoEntity, "createEntity", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("id", (type) => String))
], MongoEntity, "removeEntity", 1);
MongoEntity = __decorateClass([
  Entity()
], MongoEntity);
export {
  MongoEntity
};
